/**
 * MessageBubble: o balão de uma mensagem do WhatsApp com tudo o que vem junto (cabeçalho de texto
 * ou mídia, localização, corpo, rodapé, hora, botões, botão de lista e pedido de ligação).
 *
 * É a única peça que desenha a mensagem. O telefone (`WhatsAppPhone`) o usa à esquerda, como o
 * cliente recebe, e o cartão de envio da Inbox (`ChatPreview`) à direita, como o agente envia. Os
 * temas vêm das variáveis CSS `--wa-*` de `chatTheme.js`, que o elemento pai precisa definir.
 *
 * Os botões só respondem ao toque quando `onButton` é passado (a simulação do telefone). Sem ele
 * são só desenho, para o cartão de envio não parecer clicável.
 */

import React from "react";
import { Copy, ExternalLink, FileText, Image as ImageIcon, List as ListIcon, MapPin, Phone, Play, Reply, Video } from "lucide-react";
import { renderTemplateText } from "./templateText";

export const BUBBLE_SHADOW = "0 1px 0.5px rgba(11,20,26,0.13)";
export const TAIL_IN = "polygon(100% 0, 0 0, 100% 100%)";
export const TAIL_OUT = "polygon(0 0, 100% 0, 0 100%)";

/** Hora no formato da conversa, no fuso de Brasília. */
export function formatTime(date) {
  return date.toLocaleTimeString("pt-BR", {
    hour: "2-digit",
    minute: "2-digit",
    timeZone: "America/Sao_Paulo",
  });
}

function buttonIcon(button) {
  switch (button.type) {
    case "URL":
      return ExternalLink;
    case "PHONE_NUMBER":
      return Phone;
    case "COPY_CODE":
      return Copy;
    default:
      return Reply;
  }
}

/** Localização: mapa esquemático com o pino, o nome e o endereço. É uma prévia, não busca mapa de verdade. */
function LocationCard({ name, address }) {
  return (
    <>
      <div
        data-testid="whatsapp-location-map"
        className="mb-1 flex h-[92px] items-center justify-center overflow-hidden rounded-[5px] text-red-500"
        style={{ background: "var(--wa-var-bg)" }}
      >
        <MapPin className="h-7 w-7" />
      </div>
      {(name || address) && (
        <div className="px-[5px] pt-0.5">
          {name && <div className="text-[13px] font-semibold">{name}</div>}
          {address && (
            <div className="text-[11.5px]" style={{ color: "var(--wa-sub)" }}>
              {address}
            </div>
          )}
        </div>
      )}
    </>
  );
}

/** O nome do arquivo para mostrar: o nome salvo, ou o último pedaço da URL, ou um nome genérico. */
function fileLabel(mediaName, mediaUrl) {
  if (mediaName) return mediaName;
  if (mediaUrl && !mediaUrl.startsWith("data:") && !mediaUrl.startsWith("blob:")) {
    const raw = mediaUrl.split("?")[0].split("/").pop() || "";
    try {
      const last = decodeURIComponent(raw);
      if (last) return last;
    } catch {
      // `%` solto na URL: usa o pedaço como está, em vez de derrubar o balão.
      if (raw) return raw;
    }
  }
  return "Documento.pdf";
}

function HeaderMedia({ format, mediaUrl, mediaName }) {
  if (format === "IMAGE") {
    return (
      <div className="mb-1 overflow-hidden rounded-[5px] bg-neutral-500/30">
        {mediaUrl ? (
          <img src={mediaUrl} alt="Imagem do cabeçalho" className="h-auto w-full object-cover" style={{ maxHeight: 260 }} />
        ) : (
          <div className="flex aspect-video items-center justify-center text-neutral-400">
            <ImageIcon className="h-8 w-8" />
          </div>
        )}
      </div>
    );
  }
  if (format === "VIDEO") {
    return (
      <div className="mb-1 overflow-hidden rounded-[5px] bg-neutral-500/30">
        {mediaUrl ? (
          <video src={mediaUrl} controls className="h-auto w-full" style={{ maxHeight: 260 }} />
        ) : (
          <div className="relative flex aspect-video items-center justify-center text-neutral-300">
            <Video className="h-8 w-8" />
            <span className="absolute inset-0 m-auto flex h-10 w-10 items-center justify-center rounded-full bg-black/50">
              <Play className="h-5 w-5 fill-white text-white" />
            </span>
          </div>
        )}
      </div>
    );
  }
  if (format === "DOCUMENT") {
    return (
      <div className="mb-1 flex items-center gap-2 rounded-[5px] bg-black/10 p-2.5">
        <FileText className="h-6 w-6 shrink-0" style={{ color: "var(--wa-sub)" }} />
        <span className="min-w-0 truncate text-xs">{fileLabel(mediaName, mediaUrl)}</span>
      </div>
    );
  }
  return null;
}

const BUTTON_CLASS =
  "flex min-h-10 items-center justify-center gap-[7px] rounded-lg px-2.5 text-center text-[13px] font-semibold";
const BUTTON_ACTIVE_CLASS =
  "transition hover:brightness-95 active:scale-[0.985] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-1";

/**
 * @param {object} props
 * @param {object} props.template modelo no formato da Meta (`components[]`, mais LIST e LOCATION)
 * @param {{ values?: Record<string,string>, mode?: 'example'|'model' }} [props.ctx] variáveis do texto
 * @param {string} props.time hora do balão
 * @param {'in'|'out'} [props.direction] `in`: recebida (esquerda); `out`: enviada (direita)
 * @param {(button: object, index: number) => void} [props.onButton] liga o toque nos botões
 * @param {boolean} [props.listOpen] a folha da lista está aberta (só vale com `onToggleList`)
 * @param {() => void} [props.onToggleList] liga o botão da lista
 * @param {() => void} [props.onCallPermission] liga o botão "Permitir ligações"
 */
export function MessageBubble({ template, ctx, time, direction = "in", onButton, listOpen = false, onToggleList, onCallPermission }) {
  const components = template?.components;
  const headerComp = components?.find((c) => c.type === "HEADER");
  const bodyComp = components?.find((c) => c.type === "BODY");
  const footerComp = components?.find((c) => c.type === "FOOTER");
  const buttons = components?.find((c) => c.type === "BUTTONS")?.buttons || [];
  const hasCallPermission = components?.some((c) => c.type === "call_permission_request");
  const locationComp = components?.find((c) => c.type === "LOCATION");
  const listComp = components?.find((c) => c.type === "LIST");
  // Localização e mídia sem legenda não têm corpo: não mostram o "Sem conteúdo".
  // Um template sempre traz BODY (mesmo vazio), então as telas de Templates seguem como antes.
  const showBody = Boolean(bodyComp) || (!locationComp && !headerComp);
  const mediaUrl = headerComp?.example?.header_handle?.[0] || headerComp?.previewUrl || null;
  const mediaName = headerComp?.mediaName || null;

  const outgoing = direction === "out";
  const surface = outgoing ? "var(--wa-out)" : "var(--wa-in)";
  const buttonStyle = { background: "var(--wa-in)", color: "var(--wa-link)", boxShadow: BUBBLE_SHADOW, outlineColor: "var(--wa-link)" };
  // Cada botão só vira `button` quando o seu próprio gatilho foi passado: sem ele é desenho.
  const tagFor = (handler) => (typeof handler === "function" ? "button" : "div");
  const classFor = (handler) => `${BUTTON_CLASS} ${typeof handler === "function" ? BUTTON_ACTIVE_CLASS : ""}`;
  const propsFor = (handler, extra = {}) => (typeof handler === "function" ? { type: "button", onClick: handler, ...extra } : {});
  const ListTag = tagFor(onToggleList);
  const ButtonTag = tagFor(onButton);
  const CallTag = tagFor(onCallPermission);

  return (
    <div className="flex flex-col gap-0.5" style={{ maxWidth: "88%", alignSelf: outgoing ? "flex-end" : "flex-start" }} data-direction={direction}>
      <div
        className={`relative rounded-lg p-1 text-[13px] leading-[1.34] ${outgoing ? "rounded-tr-none" : "rounded-tl-none"}`}
        style={{ background: surface, color: "var(--wa-in-fg)", boxShadow: BUBBLE_SHADOW }}
      >
        <span
          aria-hidden="true"
          className={`absolute top-0 h-3 w-2 ${outgoing ? "-right-[7px]" : "-left-[7px]"}`}
          style={{ background: surface, clipPath: outgoing ? TAIL_OUT : TAIL_IN }}
        />
        {headerComp && headerComp.format !== "TEXT" && <HeaderMedia format={headerComp.format} mediaUrl={mediaUrl} mediaName={mediaName} />}
        {headerComp?.format === "TEXT" && headerComp.text && (
          <div className="px-[5px] pb-1 pt-[3px] text-sm font-bold">{renderTemplateText(headerComp.text, ctx)}</div>
        )}
        {locationComp && <LocationCard name={locationComp.name} address={locationComp.address} />}
        {showBody && (
          <div className="whitespace-pre-wrap break-words px-[5px] pt-0.5" data-testid="whatsapp-body">
            {bodyComp?.text ? renderTemplateText(bodyComp.text, ctx) : "Sem conteúdo"}
          </div>
        )}
        {footerComp?.text && (
          <div className="px-[5px] pt-[3px] text-[11.5px]" style={{ color: "var(--wa-meta)" }}>
            {footerComp.text}
          </div>
        )}
        <div className="-mt-px flex justify-end px-[5px] pb-px text-[10.5px]" style={{ color: "var(--wa-meta)" }}>
          {time}
        </div>
      </div>

      {(buttons.length > 0 || hasCallPermission || listComp) && (
        <div className="flex flex-col gap-0.5">
          {listComp && (
            <ListTag {...propsFor(onToggleList, { "aria-expanded": listOpen })} className={classFor(onToggleList)} style={buttonStyle}>
              <ListIcon className="h-[15px] w-[15px] shrink-0" />
              <span className="min-w-0 break-words">{listComp.button || "Ver opções"}</span>
            </ListTag>
          )}
          {buttons.map((button, index) => {
            const Icon = buttonIcon(button);
            return (
              <ButtonTag
                key={`${button.type}-${index}`}
                {...propsFor(typeof onButton === "function" ? () => onButton(button, index) : undefined)}
                className={classFor(onButton)}
                style={buttonStyle}
              >
                <Icon className="h-[15px] w-[15px] shrink-0" />
                <span className="min-w-0 break-words">{button.text}</span>
              </ButtonTag>
            );
          })}
          {hasCallPermission && (
            <CallTag {...propsFor(onCallPermission)} className={classFor(onCallPermission)} style={buttonStyle}>
              <Phone className="h-[15px] w-[15px] shrink-0" />
              Permitir ligações
            </CallTag>
          )}
        </div>
      )}
    </div>
  );
}
